// Footer.js
import React from 'react';
//import DOMPurify from 'dompurify';

/**
 * Footer Component
 * 
 * This component renders a footer section for a web page. It is built using React functional component syntax.
 * 
 * Props:
 * - `copyrightText`: A string that represents the copyright text to be displayed in the footer. 
 *    Default value is an empty string.
 * - `attachCurrentYear`: A boolean that determines whether the current year should be appended to the 
 *    copyright text. Default value is true.
 * - `FooterLinks`: An array of objects where each object represents a link in the footer. Each object 
 *    should have `linkTargetUrl` and `linkText` properties. Default value is an empty array.
 * 
 * The component structure is as follows:
 * - The top-level `<div>` acts as a container with a set of predefined classes for styling.
 * - Inside this container, a `<footer>` element is defined with a class for additional styling.
 * - Within the `<footer>`, a grid layout is established using `<div>` elements with classes indicating 
 *   their role in a grid system (likely from a CSS framework like Bootstrap or a custom AEM grid).
 * - The footer is divided into sections, with the main section containing:
 *   - A horizontal rule (`<hr>`) for visual separation.
 *   - A navigation (`<nav>`) element for footer links, where each link is rendered based on the `FooterLinks` prop.
 *   - A copyright section that conditionally displays the current year alongside the copyright text, 
 *     based on the `attachCurrentYear` prop.
 *   - An additional `<div>` for a phone link, which is currently empty but structured for potential future use.
 * 
 * Usage Example:
 * 
 * ```jsx
 * <Footer 
 *   copyrightText="© Your Company"
 *   attachCurrentYear={true}
 *   FooterLinks={[
 *     { linkTargetUrl: "/terms", linkText: "Terms & Conditions" },
 *     { linkTargetUrl: "/privacy", linkText: "Privacy Policy" }
 *   ]}
 * />
 * ```
 * 
 * Note:
 * - The component uses modern JavaScript features like arrow functions, destructuring assignment, and 
 *   template literals for readability and efficiency.
 * - It is assumed that styling is handled externally, as classes are used extensively but not defined within this component.
 * - The `key` prop in the list of footer links uses the index of the map function. For larger lists or lists that can change, 
 *   consider using a more stable identifier to avoid potential issues with React's rendering optimization.
 */
const Footer = ({ copyrightText = '', attachCurrentYear = true, FooterLinks = []}) => (
  <div className="footer parsys aem-GridColumn aem-GridColumn--default--12">
      <footer className="c-footer">
          <div className="aem-Grid aem-Grid--12 aem-Grid--default--12 ">
              <div className="footer-legal parbase aem-GridColumn aem-GridColumn--default--12">
                  <div>
                      <hr className="c-divider c-divider--compact c-footer__divider" role="presentation" aria-hidden="true" />
                  </div>
                  <div className="c-footer__service">
                      <nav className="c-footer__legal" role="navigation" aria-label="Service Links">
                      {FooterLinks.map((footerLink, index) => (
                          <a key={index} className="c-footer__legal-link" href={footerLink.linkTargetUrl}>
                            {footerLink.linkText}
                          </a>
                      ))}
                      </nav>
                      <div className="c-footer__copyright">
                          <small className="c-copyright" role="contentinfo">
                            {attachCurrentYear ? `${copyrightText} ${new Date().getFullYear()}` : copyrightText}
                          </small>
                      </div>
                      <div className="c-phone-link">
                          <span id="footer-phone-link" className="c-phone-link__title c-phone-link__title--desktop">
                          </span>
                      </div>
                  </div>
              </div>
          </div>
      </footer>
  </div>
);

export default Footer;